import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Search GAINS raw data tables.
 *
 * Each GAINS file type (ONHNLOTS, INITINFC, etc.) is stored in its own
 * staging table with raw column names. This contract provides a single
 * search endpoint that works across all file types, returning dynamic
 * columns so the frontend can render any table without prior knowledge
 * of the schema.
 */

// ─── Shared schemas ─────────────────────────────────────────────────────────

export const GainsFileTypeSchema = z.enum([
  "onhnlots",
  "initinfc",
  "initinfcs",
  "dmdtrans",
  "dmdtransnl",
  "dmdtransls",
  "suptrans",
  "subchain",
  "vendinfc",
  "skusupparm",
  "vendmast",
  "onhnunsh",
]);

export type GainsFileType = z.infer<typeof GainsFileTypeSchema>;

const PaginationSchema = z.object({
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  offset: z.number().int().nonnegative(),
  hasMore: z.boolean(),
});

const ColumnSchema = z.object({
  key: z.string(),
  label: z.string(),
});

// ─── Search ─────────────────────────────────────────────────────────────────

export const GainsRawDataSearchInputSchema = z.object({
  fileType: GainsFileTypeSchema,
  division: z.string().optional(),
  /** Free-text search across key fields */
  search: z.string().optional(),
  /** Dynamic column filters: raw column name → substring search value */
  filters: z.record(z.string(), z.string()).optional(),
  /** Faceted filters: raw column name → list of exact values (server-side IN) */
  facetFilters: z.record(z.string(), z.array(z.string())).optional(),
  limit: z.number().int().min(1).max(500).optional().default(100),
  offset: z.number().int().min(0).optional().default(0),
  sortBy: z.string().optional(),
  sortDirection: z.enum(["asc", "desc"]).optional().default("asc"),
});

export const GainsRawDataSearchResponseSchema = z.object({
  rows: z.array(
    z.record(z.string(), z.union([z.string(), z.number(), z.null()])),
  ),
  pagination: PaginationSchema,
  /** Column metadata so the frontend knows what columns exist and their display names */
  columns: z.array(ColumnSchema),
});

export type GainsRawDataSearchInput = z.input<
  typeof GainsRawDataSearchInputSchema
>;
export type GainsRawDataSearchResponse = z.infer<
  typeof GainsRawDataSearchResponseSchema
>;

// ─── Distinct values ───────────────────────────────────────────────────────

export const GainsDistinctValuesInputSchema = z.object({
  fileType: GainsFileTypeSchema,
  column: z.string(),
  division: z.string().optional(),
});

export const GainsDistinctValuesResponseSchema = z.object({
  values: z.array(z.string()),
});

export type GainsDistinctValuesInput = z.input<
  typeof GainsDistinctValuesInputSchema
>;
export type GainsDistinctValuesResponse = z.infer<
  typeof GainsDistinctValuesResponseSchema
>;

// ─── Contract ───────────────────────────────────────────────────────────────

export const gainsRawDataSearchContracts = {
  search: oc
    .input(GainsRawDataSearchInputSchema)
    .output(GainsRawDataSearchResponseSchema)
    .route({
      method: "GET",
      summary:
        "Search GAINS raw data staging tables by file type with dynamic column filters and free-text search.",
    }),
  distinctValues: oc
    .input(GainsDistinctValuesInputSchema)
    .output(GainsDistinctValuesResponseSchema)
    .route({
      method: "GET",
      summary: "Get distinct values for a column in a GAINS raw data table.",
    }),
} as const;
